import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Building2, Car, Home, ArrowRight, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { API_URL } from '../lib/utils';

const QuotePage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [quoteType, setQuoteType] = useState('');
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    property_address: '', property_value: '', year_built: '', square_feet: '',
    vehicle_year: '', vehicle_make: '', vehicle_model: '', driving_history: 'clean', annual_mileage: '',
    business_name: '', business_type: '', annual_revenue: '', employee_count: ''
  });

  const insuranceTypes = [
    { id: 'home', label: 'Home Insurance', icon: Home },
    { id: 'auto', label: 'Auto Insurance', icon: Car },
    { id: 'business', label: 'Business Insurance', icon: Building2 },
  ];

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleSelectChange = (name, value) => setFormData({ ...formData, [name]: value });
  const handleTypeSelect = (type) => { setQuoteType(type); setStep(2); };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const requestData = { quote_type: quoteType, first_name: formData.first_name, last_name: formData.last_name, email: formData.email, phone: formData.phone };
      if (quoteType === 'home') {
        requestData.property_address = formData.property_address;
        requestData.property_value = parseFloat(formData.property_value) || 0;
        requestData.year_built = parseInt(formData.year_built) || 2000;
        requestData.square_feet = parseInt(formData.square_feet) || 0;
      } else if (quoteType === 'auto') {
        requestData.vehicle_year = parseInt(formData.vehicle_year) || 2020;
        requestData.vehicle_make = formData.vehicle_make;
        requestData.vehicle_model = formData.vehicle_model;
        requestData.driving_history = formData.driving_history;
        requestData.annual_mileage = parseInt(formData.annual_mileage) || 12000;
      } else {
        requestData.business_name = formData.business_name;
        requestData.business_type = formData.business_type;
        requestData.annual_revenue = parseFloat(formData.annual_revenue) || 0;
        requestData.employee_count = parseInt(formData.employee_count) || 1;
      }
      const response = await fetch(`${API_URL}/quotes`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(requestData) });
      if (!response.ok) throw new Error('Quote generation failed');
      const quote = await response.json();
      toast.success('Quote generated successfully!');
      navigate(`/quote/result/${quote.id}`);
    } catch (error) {
      toast.error(error.message || 'Failed to generate quote');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return !!quoteType;
    if (step === 2) return formData.first_name && formData.last_name && formData.email;
    return true;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="page-banner">
        <div className="container-wide">
          <h1>GET A QUOTE</h1>
          <div className="breadcrumb"><Link to="/">Home</Link> &gt; Quote</div>
        </div>
      </div>

      <section className="content-section" data-testid="quote-content">
        <div className="container-narrow" style={{maxWidth: '600px'}}>
          
          {/* Progress */}
          <div className="flex items-center justify-center gap-4 mb-10">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  s < step ? 'bg-[#1a365d] text-white' : s === step ? 'bg-[var(--gold)] text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {s < step ? <CheckCircle className="h-5 w-5" /> : s}
                </div>
                {s < 3 && <div className={`w-12 h-1 ${s < step ? 'bg-[#1a365d]' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>

          {/* Step 1 */}
          {step === 1 && (
            <>
              <h2 className="text-center">What type of insurance do you need?</h2>
              <div className="grid grid-cols-3 gap-4 mt-8">
                {insuranceTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => handleTypeSelect(type.id)}
                    className={`border-2 p-6 text-center transition-all hover:border-[#1a365d] ${
                      quoteType === type.id ? 'border-[#1a365d] bg-gray-50' : 'border-gray-200'
                    }`}
                    data-testid={`quote-type-${type.id}`}
                  >
                    <type.icon className="h-10 w-10 mx-auto mb-3 text-[#1a365d]" strokeWidth={1} />
                    <p className="font-semibold text-[#1a365d] text-sm">{type.label}</p>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <>
              <h2 className="text-center">Your Contact Information</h2>
              <div className="space-y-4 mt-8">
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="form-label">First Name</label><Input name="first_name" value={formData.first_name} onChange={handleChange} placeholder="John" className="form-input" required data-testid="quote-first-name" /></div>
                  <div><label className="form-label">Last Name</label><Input name="last_name" value={formData.last_name} onChange={handleChange} placeholder="Doe" className="form-input" required data-testid="quote-last-name" /></div>
                </div>
                <div><label className="form-label">Email</label><Input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="you@example.com" className="form-input" required data-testid="quote-email" /></div>
                <div><label className="form-label">Phone (Optional)</label><Input name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="(617) 555-0123" className="form-input" data-testid="quote-phone" /></div>
              </div>
            </>
          )}

          {/* Step 3 */}
          {step === 3 && (
            <>
              <h2 className="text-center">{quoteType === 'home' ? 'Property' : quoteType === 'auto' ? 'Vehicle' : 'Business'} Details</h2>
              <div className="space-y-4 mt-8">
                {quoteType === 'home' && (
                  <>
                    <div><label className="form-label">Property Address</label><Input name="property_address" value={formData.property_address} onChange={handleChange} placeholder="123 Main St, Boston, MA" className="form-input" data-testid="quote-property-address" /></div>
                    <div><label className="form-label">Estimated Property Value ($)</label><Input name="property_value" type="number" value={formData.property_value} onChange={handleChange} placeholder="500000" className="form-input" data-testid="quote-property-value" /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><label className="form-label">Year Built</label><Input name="year_built" type="number" value={formData.year_built} onChange={handleChange} placeholder="1990" className="form-input" data-testid="quote-year-built" /></div>
                      <div><label className="form-label">Square Feet</label><Input name="square_feet" type="number" value={formData.square_feet} onChange={handleChange} placeholder="2000" className="form-input" data-testid="quote-square-feet" /></div>
                    </div>
                  </>
                )}
                {quoteType === 'auto' && (
                  <>
                    <div className="grid grid-cols-3 gap-4">
                      <div><label className="form-label">Year</label><Input name="vehicle_year" type="number" value={formData.vehicle_year} onChange={handleChange} placeholder="2022" className="form-input" data-testid="quote-vehicle-year" /></div>
                      <div><label className="form-label">Make</label><Input name="vehicle_make" value={formData.vehicle_make} onChange={handleChange} placeholder="Toyota" className="form-input" data-testid="quote-vehicle-make" /></div>
                      <div><label className="form-label">Model</label><Input name="vehicle_model" value={formData.vehicle_model} onChange={handleChange} placeholder="Camry" className="form-input" data-testid="quote-vehicle-model" /></div>
                    </div>
                    <div><label className="form-label">Driving History</label>
                      <Select value={formData.driving_history} onValueChange={(v) => handleSelectChange('driving_history', v)}>
                        <SelectTrigger data-testid="quote-driving-history"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="clean">Clean Record</SelectItem>
                          <SelectItem value="minor_violations">Minor Violations</SelectItem>
                          <SelectItem value="accident">Recent Accident</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><label className="form-label">Annual Mileage</label><Input name="annual_mileage" type="number" value={formData.annual_mileage} onChange={handleChange} placeholder="12000" className="form-input" data-testid="quote-annual-mileage" /></div>
                  </>
                )}
                {quoteType === 'business' && (
                  <>
                    <div><label className="form-label">Business Name</label><Input name="business_name" value={formData.business_name} onChange={handleChange} placeholder="Acme Corp" className="form-input" data-testid="quote-business-name" /></div>
                    <div><label className="form-label">Business Type</label>
                      <Select value={formData.business_type} onValueChange={(v) => handleSelectChange('business_type', v)}>
                        <SelectTrigger data-testid="quote-business-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="retail">Retail</SelectItem>
                          <SelectItem value="restaurant">Restaurant</SelectItem>
                          <SelectItem value="professional_services">Professional Services</SelectItem>
                          <SelectItem value="construction">Construction</SelectItem>
                          <SelectItem value="technology">Technology</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><label className="form-label">Annual Revenue ($)</label><Input name="annual_revenue" type="number" value={formData.annual_revenue} onChange={handleChange} placeholder="500000" className="form-input" data-testid="quote-annual-revenue" /></div>
                    <div><label className="form-label">Number of Employees</label><Input name="employee_count" type="number" value={formData.employee_count} onChange={handleChange} placeholder="10" className="form-input" data-testid="quote-employee-count" /></div>
                  </>
                )}
              </div>
            </>
          )}

          <hr className="content-divider" />

          {/* Navigation */}
          <div className="flex justify-between">
            <button onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} className="btn-outline-navy flex items-center gap-2 disabled:opacity-50" data-testid="quote-back-btn">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            {step < 3 ? (
              <button onClick={() => setStep(step + 1)} disabled={!canProceed()} className="btn-navy flex items-center gap-2 disabled:opacity-50" data-testid="quote-next-btn">
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={loading} className="btn-navy flex items-center gap-2" data-testid="quote-submit-btn">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating...</> : 'Get Quote'}
              </button>
            )}
          </div>

          <p className="text-xs text-gray-500 text-center mt-8">
            This is an estimate only. Not a binding quote until reviewed by a licensed agent.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default QuotePage;
